import React, { useState, useEffect, useRef, useCallback } from 'react';
import { VscTrash, VscCloudDownload, VscEdit, VscCheck, VscClose, VscCloudUpload } from 'react-icons/vsc';
import { getFiles, uploadFiles, updateFile, deleteFile } from '../../api/projectFiles';
import FilePrintButton from './FilePrintButton';
import './FileManager.css';

function formatSize(bytes) {
  if (!bytes) return '0 B';
  const k = 1024, sizes = ['B','KB','MB','GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function InlineEdit({ value, placeholder, onSave, onCancel }) {
  const [val, setVal] = useState(value || '');
  return (
    <div className="fm-note-edit">
      <input className="fm-note-input" value={val} autoFocus placeholder={placeholder}
        onChange={e => setVal(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') onSave(val); if (e.key === 'Escape') onCancel(); }} />
      <button className="fl-btn-icon fl-btn-save" onClick={() => onSave(val)}><VscCheck size={13} /></button>
      <button className="fl-btn-icon" onClick={onCancel}><VscClose size={13} /></button>
    </div>
  );
}

const CAN_UPLOAD_ROLES = new Set(['admin', 'engineer', 'manager', 'foreman']);

export default function FileManager({ folder, project, isActive, onOpenTab, role }) {
  const [files, setFiles]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [dragging, setDragging]   = useState(false);
  const [editing, setEditing]     = useState(null); // { id, field }
  const fileInputRef = useRef(null);
  const loadedRef    = useRef(false);
  const canUpload = CAN_UPLOAD_ROLES.has(role);

  const load = useCallback(async () => {
    if (!folder || !project) return;
    setLoading(true);
    try { setFiles(await getFiles(project._id, folder._id)); }
    catch {}
    setLoading(false);
  }, [folder?._id, project?._id]);

  useEffect(() => {
    if (isActive || !loadedRef.current) { loadedRef.current = true; load(); }
  }, [isActive, load]);

  const handleUpload = async (fileList) => {
    if (!fileList?.length) return;
    setUploading(true); setUploadError('');
    try { await uploadFiles(project._id, folder._id, fileList); await load(); }
    catch (err) { setUploadError(err?.response?.data?.msg || 'Upload failed.'); }
    setUploading(false);
  };

  const handleDragOver  = (e) => { if (!canUpload) return; e.preventDefault(); setDragging(true); };
  const handleDragLeave = (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false); };
  const handleDrop      = (e) => { e.preventDefault(); setDragging(false); if (canUpload) handleUpload(e.dataTransfer.files); };

  const handleDelete = async (file) => {
    if (!confirm(`Delete "${file.filename}"?`)) return;
    await deleteFile(file._id);
    setFiles(prev => prev.filter(f => f._id !== file._id));
  };

  const startEdit = (id, field, value) => setEditing({ id, field, value });
  const cancelEdit = () => setEditing(null);

  const saveEdit = async (file, field, val) => {
    const updated = await updateFile(file._id, { [field]: val });
    setFiles(prev => prev.map(f => f._id === file._id ? updated : f));
    setEditing(null);
  };

  const openFile = (file) => {
    const ext = file.filename.toLowerCase().split('.').pop();
    if (ext === 'pdf') {
      if (navigator.maxTouchPoints > 0) {
        const token = localStorage.getItem('kspro_token');
        window.open(`/api/project-files/${file._id}/download?token=${encodeURIComponent(token)}`, '_blank');
        return;
      }
      if (onOpenTab) onOpenTab(`pdf-${file._id}`, file.filename, { type: 'pdf-viewer', file });
    } else if (ext === 'txt' && onOpenTab) {
      onOpenTab(`txt-${file._id}`, file.filename, { type: 'txt-viewer', file });
    } else {
      downloadFile(file);
    }
  };

  const downloadFile = async (file) => {
    const token = localStorage.getItem('kspro_token');
    const res = await fetch(`/api/project-files/${file._id}/download`, { headers: { 'x-auth-token': token } });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = file.filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  const totalSize = files.reduce((s, f) => s + (f.size || 0), 0);

  const isEditing = (id, field) => editing?.id === id && editing?.field === field;

  return (
    <div className={`fm-root${dragging ? ' fm-dragging' : ''}`}
      onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>

      <div className="fm-header">
        <span className="fm-title">{folder?.name}</span>
        <span className="fm-meta">{files.length} file{files.length !== 1 ? 's' : ''} · {formatSize(totalSize)}</span>
        {canUpload && (
          <>
            <button className="btn-primary fm-upload-btn" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
              <VscCloudUpload size={14} style={{ marginRight: 5 }} />
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
            <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }}
              onChange={e => { handleUpload(Array.from(e.target.files)); e.target.value = ''; }} />
          </>
        )}
      </div>

      {uploadError && <div className="fm-error">{uploadError}</div>}
      {dragging && canUpload && <div className="fm-drop-overlay"><VscCloudUpload size={40} /><div>Drop files to upload</div></div>}

      {loading ? (
        <div className="fm-loading">Loading…</div>
      ) : files.length === 0 ? (
        <div className="fm-empty">
          <VscCloudUpload size={36} />
          <div>No files yet</div>
          <div className="fm-empty-hint">Click Upload or drag files here</div>
        </div>
      ) : (
        <div className="fm-table-wrap">
          <table className="fm-table">
            <thead>
              <tr>
                <th>Name</th><th>Rev.</th><th>Revision Note</th>
                <th>Size</th><th>Uploaded</th><th>By</th><th></th>
              </tr>
            </thead>
            <tbody>
              {files.map(file => (
                <tr key={file._id} className="fm-row">

                  {/* Filename */}
                  <td className="fm-cell-name">
                    {isEditing(file._id, 'filename') ? (
                      <InlineEdit value={file.filename} onSave={v => saveEdit(file, 'filename', v)} onCancel={cancelEdit} />
                    ) : (
                      <div className="fm-name-wrap">
                        <span className="fm-filename" title={file.filename} onClick={() => openFile(file)}>
                          {file.filename}
                        </span>
                        <button className="fl-btn-icon fm-rename-btn" title="Rename"
                          onClick={() => startEdit(file._id, 'filename', file.filename)}>
                          <VscEdit size={12} />
                        </button>
                      </div>
                    )}
                  </td>

                  {/* Revision */}
                  <td className="fm-cell-rev">
                    {isEditing(file._id, 'revision') ? (
                      <InlineEdit value={file.revision} placeholder="Rev…"
                        onSave={v => saveEdit(file, 'revision', v)} onCancel={cancelEdit} />
                    ) : (
                      <span onClick={() => startEdit(file._id, 'revision', file.revision)}
                        title="Click to edit revision" style={{ cursor: 'pointer' }}>
                        {file.revision ? <span className="fm-rev-badge">{file.revision}</span> : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </span>
                    )}
                  </td>

                  {/* Revision Note */}
                  <td className="fm-cell-revnote">
                    {isEditing(file._id, 'revisionNote') ? (
                      <InlineEdit value={file.revisionNote} placeholder="Revision note…"
                        onSave={v => saveEdit(file, 'revisionNote', v)} onCancel={cancelEdit} />
                    ) : (
                      <span className={`fm-note-text${file.revisionNote ? '' : ' fm-note-empty'}`}
                        onClick={() => startEdit(file._id, 'revisionNote', file.revisionNote)}
                        title="Click to edit revision note">
                        {file.revisionNote || '—'}
                      </span>
                    )}
                  </td>

                  <td className="fm-cell-size">{formatSize(file.size)}</td>
                  <td className="fm-cell-date">{new Date(file.createdAt).toLocaleDateString()}</td>
                  <td className="fm-cell-user">{file.uploadedBy?.username || '—'}</td>
                  <td className="fm-cell-actions">
                    {file.filename?.toLowerCase().endsWith('.pdf') && (
                      <FilePrintButton fileId={file._id} isPersonal={false} role={role} />
                    )}
                    <button className="fl-btn-icon" title="Download" onClick={() => downloadFile(file)}>
                      <VscCloudDownload size={14} />
                    </button>
                    <button className="fl-btn-icon fl-btn-del" title="Delete" onClick={() => handleDelete(file)}>
                      <VscTrash size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
